import axios, { AxiosInstance } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { attachAuthInterceptor } from '@shared/auth/tokenRefresh';

const axiosClient: AxiosInstance = axios.create();

// Correlation id on every request, on BOTH instances below.
function attachCorrelationId(client: AxiosInstance): void {
    client.interceptors.request.use(
        (config) => {
            config.headers['x-nq-correlation-id'] = uuidv4();
            return config;
        }, null, { synchronous: true }
        // When you add request interceptors, they are presumed to be asynchronous by default. This can cause a delay in the execution of your axios request when the main thread is blocked (a promise is created under the hood for the interceptor and your request gets put at the bottom of the call stack). If your request interceptors are synchronous you can add a flag to the options object that will tell axios to run the code synchronously and avoid any delays in request execution.
        // https://github.com/axios/axios#interceptors
    );
}

attachCorrelationId(axiosClient);

/**
 * Client for `/api/public/*` — NO auth interceptor.
 *
 * Those endpoints are unauthenticated by definition, and their callers are
 * public pages whose visitors have no session. Having no refresh token is the
 * NORMAL state there, which is exactly the state in which the auth interceptor
 * below gives up and navigates to /login. So any 401 from a public endpoint —
 * an HMAC rejection between the BFF and the ledger, a misconfigured signing
 * secret, or a wrong investor password — would evacuate a prospective investor
 * from the page they were reading to a login screen they have no account for.
 *
 * The risk is not hypothetical: the paper request is the first call on the
 * investor page to carry a query string into the HMAC payload, and
 * /investor/verify answers a wrong password with a 401 by design (#588).
 *
 * A separate instance rather than a per-URL skip, so a public call cannot pick
 * the interceptor up by accident, and so it never carries the `Authorization`
 * default header that login sets on `axiosClient`.
 */
export const publicAxiosClient: AxiosInstance = axios.create();

attachCorrelationId(publicAxiosClient);

// Reactive token refresh: on 401/403, refresh once and retry; otherwise clear
// the session and redirect to login. A full navigation also resets in-memory
// reporter/Faro state cleanly.
attachAuthInterceptor(axiosClient, {
    refreshUrl: '/api/auth/refresh',
    onLogout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        delete axiosClient.defaults.headers.common['Authorization'];
        window.location.assign('/login');
    },
});

export default axiosClient;
